// Copyright ©️ 2026 eVolve MEP, LLC

import { useMemo, useRef } from 'react';

import { Card, Text, Box, ActionIcon, Flex } from '@mantine/core';
import { notifications } from '@mantine/notifications';

import { helpDeskItems } from 'app/Layout/HeaderBar/HelpDeskDropdown/HelpDeskDropdown';
import { useAppShell, navbarTransitionStyle, NAV_BAR_FOOTER_HEIGHT } from 'app/Layout/NavBar/useAppShell';
import { EvolveIcon } from 'assets/icons/EvolveIcon';
import { useUserPreferences } from 'hooks/typeCacheHooks/useUserPreferences/useUserPreferences';

export const NavBarAdSpot = () => {
  const [adSpotDismissed, onChangeAdSpotDismissed, loadingAdSpotDismissed] = useUserPreferences(
    'AD_SPOT_DISMISSED',
    false,
  );
  const { navbarExpanded } = useAppShell();
  const href = useMemo(() => helpDeskItems.find((h) => h.label === 'Provide Feedback')?.href, []);
  const adSpotRef = useRef<HTMLDivElement>(null);

  return (
    <Box
      mt="xs"
      style={{
        height:
          navbarExpanded && !loadingAdSpotDismissed && !adSpotDismissed
            ? adSpotRef.current
              ? adSpotRef.current.clientHeight + 2
              : undefined
            : 0,
        opacity: navbarExpanded && !adSpotDismissed ? 1 : 0,
        transition: navbarTransitionStyle,
        overflow: 'hidden',
      }}
    >
      <Card
        ref={adSpotRef}
        w="100%"
        flex={1}
        className="ad-spot"
        py="xs"
        px="sm"
        withBorder
        onClick={() => window.open(href, '_blank')}
      >
        <Flex justify="space-between">
          <Text fw="bold" fz="sm" mb={4}>
            New Survey!
          </Text>
          <ActionIcon
            size="sm"
            onClick={async (e) => {
              e.stopPropagation();
              onChangeAdSpotDismissed(true);
              notifications.show({
                title: 'Dismissed',
                message: (
                  <>
                    You can always submit feedback via the <EvolveIcon icon="Help" inline size="sm" /> Help Desk
                  </>
                ),
                bg: '#2a292c',
                position: 'bottom-left',
                styles: {
                  root: {
                    marginBottom: NAV_BAR_FOOTER_HEIGHT,
                  },
                  title: {
                    color: 'var(--mantine-color-gray-2)',
                  },
                  description: {
                    color: 'var(--mantine-color-gray-4)',
                  },
                  closeButton: {
                    color: 'var(--mantine-color-gray-5)',
                  },
                },
              });
            }}
            color="gray.0"
          >
            <EvolveIcon size="xs" icon="Close" color="white" />
          </ActionIcon>
        </Flex>
        <Text fz="xs" c="gray.4">
          Enhance EVOLVE Shop by providing feedback.
        </Text>
      </Card>
    </Box>
  );
};
